/**
 * Course between two underwater objects: the compass heading to swim, the distance, the depth change.
 * Everything is computed client-side from the recorded positions; no contract is involved.
 *
 * Method (documented choices):
 * - TRUE bearing: the initial great-circle (forward) azimuth on a sphere of radius 6 371 008.8 m,
 *   atan2(sin dL cos f2, cos f1 sin f2 - sin f1 cos f2 cos dL). Against the WGS84 ellipsoid (Vincenty)
 *   this is off by well under 0.1 degree and 0.5 % in distance at the ranges of a dive (hundreds of
 *   metres), i.e. below the accuracy of the recorded positions.
 * - DISTANCE: haversine on the same sphere.
 * - MAGNETIC bearing = true bearing - declination (declination is positive to the east), with the
 *   declination of the World Magnetic Model WMM2025 at the start point and today's date.
 * - The way back is the bearing from B to A (not just +180: it matches within a fraction of a degree
 *   at these distances).
 */

export interface LatLon {
  lat: number;
  lon: number;
}

const R = 6_371_008.8;
const rad = (d: number) => (d * Math.PI) / 180;
const deg = (r: number) => (r * 180) / Math.PI;
export const norm360 = (d: number): number => ((d % 360) + 360) % 360;

/** Initial true bearing from `a` to `b`, degrees clockwise from geographic north, [0, 360). */
export function trueBearing(a: LatLon, b: LatLon): number {
  const f1 = rad(a.lat);
  const f2 = rad(b.lat);
  const dl = rad(b.lon - a.lon);
  const y = Math.sin(dl) * Math.cos(f2);
  const x = Math.cos(f1) * Math.sin(f2) - Math.sin(f1) * Math.cos(f2) * Math.cos(dl);
  return norm360(deg(Math.atan2(y, x)));
}

/** Horizontal distance in metres (haversine). */
export function distanceM(a: LatLon, b: LatLon): number {
  const f1 = rad(a.lat);
  const f2 = rad(b.lat);
  const h =
    Math.sin((f2 - f1) / 2) ** 2 +
    Math.cos(f1) * Math.cos(f2) * Math.sin(rad(b.lon - a.lon) / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** True -> magnetic. Declination is positive towards the east (magnetic north lies east of true north). */
export const magneticFromTrue = (trueDeg: number, declinationDeg: number): number =>
  norm360(trueDeg - declinationDeg);

export const reverseBearing = (bearingDeg: number): number => norm360(bearingDeg + 180);

/** Slope distance: the straight line through the water between the two positions. */
export const slopeDistance = (horizontalM: number, deltaDepthM: number): number =>
  Math.hypot(horizontalM, deltaDepthM);

export interface CourseEnd {
  location: LatLon;
  /** Resolved depth in metres below the surface; null when unknown. */
  depthM: number | null;
}

export interface Course {
  /** Whole degrees, magnetic: what the compass shows. */
  compassDeg: number;
  /** Whole degrees, geographic. */
  trueDeg: number;
  /** Declination used, degrees east positive, one decimal. */
  declinationDeg: number;
  /** Whole degrees, magnetic, for the swim back (from B to A). */
  backCompassDeg: number;
  /** Whole metres. */
  distanceM: number;
  depthA: number | null;
  depthB: number | null;
  /** B minus A in metres, one decimal; null when a depth is unknown. */
  deltaDepthM: number | null;
  /** Whole metres; null when a depth is unknown. */
  slopeM: number | null;
}

const round1 = (v: number) => Math.round(v * 10) / 10;

/** The course from `a` to `b` for the given declination (see `declinationAt`). */
export function course(a: CourseEnd, b: CourseEnd, declinationDeg: number): Course {
  const h = distanceM(a.location, b.location);
  const t = trueBearing(a.location, b.location);
  const back = trueBearing(b.location, a.location);
  const known = a.depthM !== null && b.depthM !== null;
  const dd = known ? (b.depthM as number) - (a.depthM as number) : null;
  return {
    compassDeg: Math.round(magneticFromTrue(t, declinationDeg)) % 360,
    trueDeg: Math.round(t) % 360,
    declinationDeg: round1(declinationDeg),
    backCompassDeg: Math.round(magneticFromTrue(back, declinationDeg)) % 360,
    distanceM: Math.round(h),
    depthA: a.depthM,
    depthB: b.depthM,
    deltaDepthM: dd === null ? null : round1(dd),
    slopeM: dd === null ? null : Math.round(slopeDistance(h, dd)),
  };
}

type GeomagModule = typeof import("geomagnetism");
let geomag: Promise<GeomagModule> | undefined;

/**
 * Magnetic declination (degrees, east positive) at a position and date from the World Magnetic Model
 * WMM2025 (npm `geomagnetism`, Apache-2.0, ships the WMM2025 coefficients). Loaded on demand: the model
 * is a few kilobytes and only needed when a course is shown.
 */
export async function declinationAt(at: LatLon, date: Date = new Date()): Promise<number> {
  geomag ??= import("geomagnetism");
  const mod = await geomag;
  const g = (mod as unknown as { default?: GeomagModule }).default ?? mod;
  return g.model(date).point([at.lat, at.lon]).decl;
}
